/* global debounce */

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.form = this.closest('form');
      // Campos vêm do Liquid (data-search-fields), que respeita as configurações de
      // SKU e tags do tema. Fallback só para o caso do atributo faltar.
      this.scopeFields = (this.dataset.searchFields || 'title,product_type,variants.title,vendor')
        .split(',')
        .map((field) => field.trim())
        .filter(Boolean);
      this.restoreDisplayTerm();
      this.addListeners();
      this.init();
    }

    addListeners() {
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.resetBtn.addEventListener('click', this.handleReset.bind(this));
      // Numa volta pelo botão Voltar o navegador restaura o valor submetido sem
      // reconstruir o componente, então o campo precisa ser limpo de novo.
      window.addEventListener('pageshow', this.restoreDisplayTerm.bind(this));
      if (this.form) {
        // Roda depois do handler do predictive-search, que é registrado antes,
        // então evt.defaultPrevented já reflete a decisão dele.
        this.form.addEventListener('submit', this.handleScopeSubmit.bind(this));
      }
    }

    init() {
      if ((this.input.dataset.placeholderTwo || this.input.dataset.placeholderThree)
        && (this.input.dataset.placeholderPromptsMob === 'true' || theme.mediaMatches.md)) {
        this.typeInPlaceholders();
      }
    }

    /**
     * Simulates the placeholder titles being typed in and the previous one deleted, on repeat
     */
    typeInPlaceholders() {
      const typingSpeed = this.input.dataset.typingSpeed || 100;
      const deletingSpeed = this.input.dataset.deletingSpeed || 60;
      const delayAfterDeleting = this.input.dataset.delayAfterDeleting || 500;
      const delayBeforeFirstDelete = this.input.dataset.delayBeforeFirstDelete || 2000;
      const delayAfterWordTyped = this.input.dataset.delayAfterWordTyped || 2400;
      const placeholders = [];

      if (this.input.placeholder) {
        placeholders.push(this.input.placeholder);
      }

      if (this.input.dataset.placeholderTwo) {
        placeholders.push(this.input.dataset.placeholderTwo);
      }

      if (this.input.dataset.placeholderThree) {
        placeholders.push(this.input.dataset.placeholderThree);
      }

      const typeInNextPlaceholder = async (placeholder) => {
        await new Promise((resolve) => {
          let currentPlaceholder = this.input.getAttribute('placeholder');

          // If necessary remove the start of the word to type in if it already exists in the
          // placeholder
          let nextPlaceholder = (currentPlaceholder.length >= 3
              && placeholder.startsWith(currentPlaceholder))
            ? placeholder.replace(currentPlaceholder, '')
            : placeholder;

          const typingIntervalId = setInterval(() => {
            currentPlaceholder = this.input.getAttribute('placeholder');
            this.input.setAttribute('placeholder', currentPlaceholder + nextPlaceholder.charAt(0));
            if (nextPlaceholder.length === 1) {
              resolve();
              clearInterval(typingIntervalId); // Stop typing
            } else {
              nextPlaceholder = nextPlaceholder.substring(1);
            }
          }, typingSpeed);
        });
      };

      const deleteCurrentPlaceholder = async (nextPlaceholder) => {
        await new Promise((resolve) => {
          let prevPlaceholder = this.input.getAttribute('placeholder');
          const deletionIntervalId = setInterval(() => {
            const newPlaceholder = prevPlaceholder.substring(0, prevPlaceholder.length - 1);
            this.input.setAttribute('placeholder', newPlaceholder);
            prevPlaceholder = newPlaceholder;
            if (prevPlaceholder.length === 0
              || (prevPlaceholder.length >= 3 && nextPlaceholder.startsWith(prevPlaceholder))) {
              resolve();
              clearInterval(deletionIntervalId);
            }
          }, deletingSpeed);
        });
      };

      let startIndex = 0;
      const showNextPlaceholder = () => {
        startIndex = (startIndex + 1) % placeholders.length;
        const nextPlaceholder = placeholders[startIndex];
        deleteCurrentPlaceholder.call(this, nextPlaceholder).then(() => {
          setTimeout(() => {
            typeInNextPlaceholder.call(this, nextPlaceholder).then(() => {
              setTimeout(showNextPlaceholder.bind(this), delayAfterWordTyped);
            });
          }, delayAfterDeleting);
        });
      };

      setTimeout(showNextPlaceholder.bind(this), delayBeforeFirstDelete);
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      if (this.input.value.length > 0) {
        this.input.classList.add('search__input--dirty');
      } else {
        this.input.classList.remove('search__input--dirty');
      }
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
    }

    /**
     * Handles the reset button being clicked
     */
    handleReset() {
      this.input.classList.remove('search__input--dirty');
    }

    /**
     * Reescreve a query no submit para o /search casar só com campos estruturados
     * e nunca com o corpo da descrição — sem isso, tabela de compatibilidade e o
     * bloco "About" compartilhado fazem aparecer peça de moto sem relação com a busca.
     *
     * NÃO trocar por `options[fields]`: esse parâmetro vale no /search/suggest, mas a
     * página /search o ignora. Medido em 2026-07-27: com "anodized" (palavra que só
     * existe no corpo) options[fields] devolveu os mesmos 22 produtos, enquanto esta
     * sintaxe devolveu 0.
     * @param {object} evt - Submit event.
     */
    handleScopeSubmit(evt) {
      // predictive-search pode ter cancelado o submit (query vazia, ou sugestão
      // escolhida pelo teclado levando para outro lugar) — não mexe nesses.
      if (evt.defaultPrevented) return;
      const raw = this.input.value.trim();
      if (!raw || SearchForm.isScopedQuery(raw)) return;
      const scoped = this.buildScopedQuery(raw);
      if (scoped) this.input.value = scoped;
    }

    /**
     * Monta a query com escopo de campos a partir dos termos digitados.
     * Cada palavra vira um OR entre os campos com `*` no fim, preservando busca por
     * prefixo ("hayab" -> Hayabusa). As palavras são unidas por AND, como o Shopify faz.
     * @param {string} raw - Termos digitados.
     * @returns {string} Query com escopo, ou '' se não sobrar nada utilizável.
     */
    buildScopedQuery(raw) {
      const tokens = raw
        .split(/\s+/)
        // Remove o que quebraria a gramática da busca ou injetaria sintaxe de campo.
        .map((token) => token.replace(/["():*]/g, '').replace(/^-+/, '').trim())
        .filter(Boolean);
      if (!tokens.length) return '';
      return tokens
        .map((token) => `(${this.scopeFields.map((field) => `${field}:${token}*`).join(' OR ')})`)
        .join(' AND ');
    }

    /**
     * True quando o valor já parece uma query gerada por este componente, para não
     * reescrevê-la de novo nem mostrar a sintaxe crua ao cliente.
     * @param {string} value - Valor a testar.
     * @returns {boolean}
     */
    static isScopedQuery(value) {
      return /\(title:[^\s)]+\*/.test(value) && value.indexOf(' OR ') !== -1;
    }

    /**
     * Recupera os termos legíveis de uma query com escopo (cada grupo começa com
     * `(title:TOKEN*`), para exibição.
     * @param {string} scoped - Query com escopo.
     * @returns {string} Termos originais separados por espaço.
     */
    static extractDisplayTerm(scoped) {
      const matches = [...scoped.matchAll(/\(title:([^\s)]+?)\*/g)];
      return matches.map((match) => match[1]).join(' ');
    }

    /**
     * Na página de resultados o `q` guarda a sintaxe com escopo; devolve ao campo
     * visível (e à mensagem de "sem resultados") os termos que o cliente digitou.
     */
    restoreDisplayTerm() {
      if (!this.input || !SearchForm.isScopedQuery(this.input.value)) return;
      const scoped = this.input.value;
      const term = SearchForm.extractDisplayTerm(scoped);
      if (!term) return;
      this.input.value = term;
      const noResults = document.querySelector('[data-search-no-results]');
      if (noResults && noResults.textContent.indexOf(scoped) !== -1) {
        noResults.textContent = noResults.textContent.replace(scoped, term);
      }
    }
  }

  customElements.define('search-form', SearchForm);
}
